(() => {
  if (customElements.get('k52-new-arrivals')) return;
  class NewArrivals extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const {signal} = this.lifecycle;
      this.links = [...this.querySelectorAll('[data-arrivals-category]')];
      this.grid = this.querySelector('[data-arrivals-grid]');
      this.status = this.querySelector('[data-arrivals-status]');
      this.retry = this.querySelector('[data-arrivals-retry]');
      this.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a');
        if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
        const category = link.dataset.arrivalsCategory;
        if (category) {
          event.preventDefault();
          this.load(category, 1, true);
        } else if (link.closest('.new-arrivals__pagination')) {
          event.preventDefault();
          const page = new URL(link.href).searchParams.get('page') || 1;
          this.load(this.dataset.category, page, true, true);
        }
      }, {signal});
      this.retry.addEventListener('click', () => {
        if (this.pending) this.load(...this.pending);
      }, {signal});
      window.addEventListener('popstate', () => this.restore(), {signal});
      const url = new URL(location.href);
      if (url.searchParams.get('sort_by') !== 'created-descending') {
        url.searchParams.set('sort_by', 'created-descending');
        location.replace(url.href);
        return;
      }
      this.restore();
      this.refreshBookmarks();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.request?.abort();
      this.resetLoading();
      clearTimeout(this.bookmarkTimer);
    }

    selectCategory(category) {
      for (const item of this.links) {
        const active = item.dataset.arrivalsCategory === category;
        item.classList.toggle('button--primary', active);
        item.classList.toggle('button--secondary', !active);
        if (active) item.setAttribute('aria-current', 'page');
        else item.removeAttribute('aria-current');
      }
    }

    resetLoading() {
      clearTimeout(this.loadingTimer);
      clearTimeout(this.requestTimer);
      clearTimeout(this.statusTimer);
      this.grid?.removeAttribute('aria-busy');
      this.grid?.classList.remove('is-filter-loading');
      if (this.status) {
        this.status.classList.remove('is-visible', 'is-loading', 'is-error');
        this.status.textContent = '';
      }
    }

    finishLoading() {
      clearTimeout(this.loadingTimer);
      clearTimeout(this.requestTimer);
      this.grid.removeAttribute('aria-busy');
      this.grid.classList.remove('is-filter-loading');
      this.status.classList.remove('is-visible');
      this.statusTimer = setTimeout(() => {
        this.status.classList.remove('is-loading', 'is-error');
        this.status.textContent = '';
      }, 180);
    }

    canonicalUrl(category, page) {
      const url = new URL(this.dataset.baseUrl, location.origin);
      if (Number(page) > 1) url.searchParams.set('page', page);
      url.hash = category === 'all' ? '' : category;
      return url;
    }

    restore() {
      const url = new URL(location.href);
      const fromHash = url.hash.slice(1);
      const category = this.links.some(link => link.dataset.arrivalsCategory === fromHash)
        ? fromHash : url.pathname === new URL(this.dataset.baseUrl, location.origin).pathname ? 'all' : this.dataset.category;
      const page = url.searchParams.get('page') || 1;
      if (category !== this.dataset.category || String(this.page || page) !== String(page)) {
        this.load(category, page, false);
      } else {
        this.request?.abort();
        this.resetLoading();
        this.selectCategory(category);
        this.retry.hidden = true;
        this.pending = null;
        this.page = page;
        history.replaceState(history.state, '', this.canonicalUrl(category, page));
      }
    }

    async load(category, page, push, scroll = false) {
      const link = this.links.find(item => item.dataset.arrivalsCategory === category);
      if (!link) return;
      if (!this.pending && category === this.dataset.category && Number(page) === Number(this.page || 1)) return;
      this.request?.abort();
      this.resetLoading();
      const controller = new AbortController();
      this.request = controller;
      this.pending = [category, page, push, scroll];
      this.grid.setAttribute('aria-busy', 'true');
      this.selectCategory(category);
      this.loadingTimer = setTimeout(() => {
        if (this.request !== controller || controller.signal.aborted || !this.isConnected) return;
        this.grid.classList.add('is-filter-loading');
        this.status.textContent = this.dataset.loading;
        this.status.classList.add('is-visible', 'is-loading');
      }, 150);
      let timedOut = false;
      const requestTimer = this.requestTimer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 15000);
      this.retry.hidden = true;
      try {
        const url = new URL(link.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('page', page);
        const response = await fetch(url, {signal: controller.signal});
        if (!response.ok) throw new Error('Collection unavailable');
        const html = await response.text();
        if (controller.signal.aborted || this.request !== controller || !this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const replacement = doc.querySelector('[data-arrivals-grid]');
        if (!replacement || replacement.dataset.category !== category) throw new Error('Invalid collection response');
        this.grid.replaceWith(replacement);
        this.grid = replacement;
        this.dataset.category = category;
        this.page = page;
        this.selectCategory(category);
        const canonical = this.canonicalUrl(category, page);
        if (push) history.pushState(history.state, '', canonical);
        else history.replaceState(history.state, '', canonical);
        this.finishLoading();
        this.pending = null;
        this.refreshBookmarks();
        this.grid.querySelector('motion-list')?.load?.()?.catch?.(() => {});
        if (scroll) this.scrollIntoView({block: 'start', behavior: 'instant'});
      } catch (error) {
        if ((controller.signal.aborted && !timedOut) || this.request !== controller || !this.isConnected) return;
        this.resetLoading();
        this.selectCategory(this.dataset.category);
        this.status.textContent = this.dataset.error;
        this.status.classList.add('is-visible', 'is-error');
        this.retry.hidden = false;
      } finally {
        clearTimeout(requestTimer);
        if (this.request === controller) this.grid.removeAttribute('aria-busy');
      }
    }

    refreshBookmarks() {
      clearTimeout(this.bookmarkTimer);
      let attempts = 0;
      const refresh = () => {
        if (!this.isConnected || attempts >= 20) return;
        const pending = [...this.grid.querySelectorAll('.th_wl_col_btn')].some(node => !node.querySelector('.wc_wishlistBlock'));
        if (!pending) return;
        attempts++;
        try {
          if (typeof window.wc_get_product_wishlist === 'function') {
            Promise.resolve(window.wc_get_product_wishlist(false)).catch(() => {});
          }
        } catch (_) {}
        this.bookmarkTimer = setTimeout(refresh, 250);
      };
      refresh();
    }
  }
  customElements.define('k52-new-arrivals', NewArrivals);
})();
